import type { ApiClient } from '@carbonwebmail/api-client'
import type {
  ConversationSummaryDto,
  DesignReferenceDto,
  DesignReplyDto,
} from '@carbonwebmail/contracts'
import { designedBody } from '@carbonwebmail/domain'
import { useCallback, useEffect, useState } from 'react'
import { attach } from './referenceFiles.js'

/**
 * Describing an email and getting one designed.
 *
 * **The conversation lives here and nowhere else.** Nothing about a
 * half-finished design is worth a table: it is worth something only once it
 * becomes a template or a campaign, and both of those already have one. The
 * cost is that a reload loses the thread; the saving is a feature with no state
 * to expire, resume or clean up. Whatever was designed is one press from being
 * saved, which is where it stops being disposable.
 *
 * **The design on screen is sent with every turn.** Without it "make the header
 * green" makes a new email with a green header rather than changing the one
 * being looked at.
 *
 * **A reply without a design is a normal turn.** Asking "which of the two
 * logos?" is more useful than guessing at a brand, so an answer with no markup
 * leaves the preview exactly as it was rather than clearing it.
 */

export interface Turn {
  /** Names of files attached to this turn, so a reopened conversation reads right. */
  readonly attached?: readonly string[]
  /**
   * Minted when the turn is made, so the list has a stable key.
   *
   * Not the array index: two turns with the same words — "shorter", said
   * twice — would share a key and React would reuse the wrong element.
   */
  readonly id: string
  readonly from: 'person' | 'designer'
  readonly text: string
}

export type DesignerState =
  | { readonly status: 'idle' }
  | { readonly status: 'thinking' }
  /** No key on the server. Not a failure, and not worth a retry button. */
  | { readonly status: 'not-configured' }
  /** The model was busy. Nothing is wrong with what was asked. */
  | { readonly status: 'busy' }
  | { readonly status: 'failed' }

/** The model is slow and the thread is long; a bounded history keeps both sane. */
const MOST_TURNS = 40

export function useDesigner({ client }: { readonly client: ApiClient }) {
  const [turns, setTurns] = useState<readonly Turn[]>([])
  const [design, setDesign] = useState<string | undefined>(undefined)
  const [state, setState] = useState<DesignerState>({ status: 'idle' })
  /**
   * Files attached to the *next* turn.
   *
   * Cleared once sent, because they belong to the instruction they went with —
   * leaving them attached would send the brand sheet again on "make it
   * shorter", and the model would read it as new evidence every turn.
   */
  const [references, setReferences] = useState<readonly DesignReferenceDto[]>([])

  /**
   * A website this email should look like it came from.
   *
   * Cleared once a turn has used it, for the same reason the files are: reading
   * a site takes seconds, and the design that comes back already carries the
   * colours and the logo — so "make it shorter" edits that rather than fetching
   * the whole site again to learn what it already knows.
   */
  const [siteUrl, setSiteUrl] = useState('')
  /** Why a file was not attached. Shown beside the picker until it is cleared. */
  const [refused, setRefused] = useState<readonly string[]>([])
  /**
   * Which stored conversation this is, once there is one.
   *
   * Minted by the server on the first answered turn rather than here: a
   * conversation that existed before its first answer would be an empty row
   * every time the model was busy.
   */
  const [conversationId, setConversationId] = useState<string | undefined>(undefined)
  /** The list beside the conversation, newest first. */
  const [saved, setSaved] = useState<readonly ConversationSummaryDto[]>([])
  /** Whether the list came back. An empty list and a failed read are different facts. */
  const [listed, setListed] = useState(false)

  const send = useCallback(
    async (instruction: string) => {
      const asked: Turn = { id: crypto.randomUUID(), from: 'person', text: instruction }
      // Taken now, so the picker empties as the turn is sent rather than when
      // the answer arrives — and so a file attached while the model is thinking
      // belongs to the next turn rather than silently joining this one.
      const carried = references
      const site = siteUrl.trim()
      setReferences([])
      setSiteUrl('')
      setRefused([])
      // Shown immediately: the model takes seconds, and a message that does not
      // appear until the answer does reads as a press that did nothing.
      const history = [...turns, asked].slice(-MOST_TURNS)
      setTurns(history)
      setState({ status: 'thinking' })

      try {
        const reply = await client.designEmail({
          instruction,
          // Everything before this turn. The instruction is sent separately, so
          // sending it here as well would show the model the same words twice.
          history: history.slice(0, -1).map((turn) => ({ from: turn.from, text: turn.text })),
          current: design ?? null,
          references: [...carried],
          ...(site === '' ? {} : { siteUrl: site }),
          ...(conversationId === undefined ? {} : { conversationId }),
        })

        if (reply === 'not-configured') {
          setState({ status: 'not-configured' })
          return
        }
        if (reply === 'busy') {
          setState({ status: 'busy' })
          return
        }
        // A file the server would not take. The browser checks the same things
        // first, so this means the two disagreed — and the server's sentence
        // names the file, which is the part worth showing.
        if ('refused' in reply) {
          setRefused([reply.refused])
          setState({ status: 'idle' })
          return
        }

        if (reply.conversationId !== null) setConversationId(reply.conversationId)
        setTurns((current) => [...current, said(reply)])
        // Only when the turn produced one. A question leaves the preview alone.
        if (reply.html !== null) setDesign(reply.html)
        setState({ status: 'idle' })
      } catch {
        setState({ status: 'failed' })
      }
    },
    // `references` belongs here: without it `send` closes over the list as it
    // was when the callback was last built, which is empty — so a file could be
    // attached, drawn as a chip, and left out of the request it was attached to.
    [client, conversationId, design, references, siteUrl, turns],
  )

  /**
   * Saving what is on screen as a template.
   *
   * A design rather than prose, with no builder project behind it — the model
   * wrote markup, not a layout the builder laid out. It opens in the builder
   * perfectly well and gains a project the first time it is saved there, and
   * claiming one it does not have would describe a layout nothing produced.
   */
  const asTemplate = useCallback(
    (name: string, subject: string) =>
      design === undefined ? undefined : { name, subject, body: designedBody(design, '') },
    [design],
  )

  /**
   * The conversations this organisation has had.
   *
   * A failure leaves the list empty and says so through `problem` rather than
   * drawing "no conversations yet" — a list that could not be read and a list
   * with nothing in it are different facts, and only one of them is about what
   * somebody has done.
   */
  const listSaved = useCallback(async () => {
    try {
      setSaved(await client.designConversations())
      setListed(true)
    } catch {
      setListed(false)
    }
  }, [client])

  useEffect(() => {
    void listSaved()
  }, [listSaved])

  const restart = useCallback(() => {
    setTurns([])
    setDesign(undefined)
    setConversationId(undefined)
    setReferences([])
    setRefused([])
    setState({ status: 'idle' })
  }, [])

  /** Opens a stored conversation, replacing whatever is on screen. */
  const open = useCallback(
    async (id: string) => {
      setState({ status: 'thinking' })
      try {
        const found = await client.designConversation(id)
        if (found === undefined) {
          // Removed in another tab. The list is stale rather than wrong.
          await listSaved()
          setState({ status: 'idle' })
          return
        }

        setTurns(
          found.turns.map((turn) => ({
            id: crypto.randomUUID(),
            from: turn.from,
            text: turn.text,
            attached: turn.attached,
          })),
        )
        setDesign(found.design ?? undefined)
        setConversationId(found.conversationId)
        setReferences([])
        setRefused([])
        setState({ status: 'idle' })
      } catch {
        setState({ status: 'failed' })
      }
    },
    [client, listSaved],
  )

  /** Removes one. If it is the one on screen, the screen starts again. */
  const forget = useCallback(
    async (id: string) => {
      try {
        await client.forgetDesignConversation(id)
        if (id === conversationId) restart()
        await listSaved()
      } catch {
        setState({ status: 'failed' })
      }
    },
    [client, conversationId, listSaved, restart],
  )

  /**
   * Adds files to the next turn, keeping what was already attached.
   *
   * Refusals replace rather than accumulate: they describe this pick, and a
   * list that grew would still be showing why a file was refused two picks ago.
   */
  const addFiles = useCallback(
    async (chosen: readonly File[]) => {
      const { references: added, refused: why } = await attach(chosen, references)
      setReferences((current) => [...current, ...added])
      setRefused(why)
    },
    [references],
  )

  const removeFile = useCallback((name: string) => {
    setReferences((current) => current.filter((one) => one.name !== name))
  }, [])

  return {
    turns,
    design,
    state,
    saved,
    listed,
    conversationId,
    open,
    forget,
    references,
    refused,
    addFiles,
    removeFile,
    siteUrl,
    setSiteUrl,
    send,
    asTemplate,
    restart,
  }
}

function said(reply: DesignReplyDto): Turn {
  return { id: crypto.randomUUID(), from: 'designer', text: reply.say }
}
